import { Dialog, DialogContent, DialogTitle } from "@mui/material";
import { useState } from "react";

import BoletoIcon from "@/assets/images/payment-methods/boleto-icon.png";
import CardIcon from "@/assets/images/payment-methods/card-icon.png";
import DefaultIcon from "@/assets/images/payment-methods/default-icon.png";
import PixIcon from "@/assets/images/payment-methods/pix-icon.png";
import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import useOrderData from "@/hooks/data/use-orders-data";
import { GroupedOrderPaymentDisplay, resolvePaymentIconKey } from "@/helpers/order-payment-display.helpers";
import { IOrder } from "@/interfaces/order.interfaces";
import { OrderPaymentMessages } from "@/messages/order-details/order-payment.messages";
import borders from "@/themes/base/borders";

type Props = {
  messages: OrderPaymentMessages;
  order: IOrder | null;
  isPrint: boolean;
};

const paymentMethodImages: Record<string, string> = {
  CARTÃO: CardIcon,
  PIX: PixIcon,
  BOLETO: BoletoIcon,
};

function PaymentGroupCard({
  group,
  messages,
  isPrint,
  onOpenDetails,
}: {
  group: GroupedOrderPaymentDisplay;
  messages: OrderPaymentMessages;
  isPrint: boolean;
  onOpenDetails: (group: GroupedOrderPaymentDisplay) => void;
}) {
  const { borderWidth, borderColor } = borders;
  const iconKey = resolvePaymentIconKey(group);
  const paymentMethodImageSrc = paymentMethodImages[iconKey] || DefaultIcon;

  return (
    <MDBox
      border={`${borderWidth[1]} solid ${borderColor}`}
      borderRadius="lg"
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      p={3}
      mt={2}
    >
      <MDBox width="10%" mr={4}>
        <img
          src={paymentMethodImageSrc}
          alt={`${group.paymentMethodName} logo`}
          width={isPrint ? 40 : 60}
          height={isPrint ? 40 : 60}
        />
      </MDBox>

      <MDBox width="100%" mr={2}>
        <MDTypography variant="h6" fontWeight="medium">
          {group.summaryLabel || messages.unknownPaymentMethodMessage}
        </MDTypography>
        <MDTypography variant="subtitle1" fontWeight="regular">
          {group.summaryAmount}
        </MDTypography>
      </MDBox>

      {!isPrint && group.paymentComments && (
        <MDButton variant="outlined" color="dark" size="small" onClick={() => onOpenDetails(group)}>
          {messages.paymentDetailsButtonLabel}
        </MDButton>
      )}
    </MDBox>
  );
}

export default function PaymentDetails({ messages, order, isPrint }: Props) {
  const { groupedPaymentSummaries } = useOrderData({ order });
  const [selectedGroup, setSelectedGroup] = useState<GroupedOrderPaymentDisplay | null>(null);

  return (
    <>
      <MDTypography variant="h6" fontWeight="medium">
        {messages.paymentInformationTitle}
      </MDTypography>

      {groupedPaymentSummaries.length === 0 ? (
        <MDBox mt={2}>
          <MDTypography variant="subtitle1">{messages.unknownPaymentMethodMessage}</MDTypography>
        </MDBox>
      ) : (
        groupedPaymentSummaries.map((group) => (
          <PaymentGroupCard
            key={group.id}
            group={group}
            messages={messages}
            isPrint={isPrint}
            onOpenDetails={setSelectedGroup}
          />
        ))
      )}

      <Dialog open={!!selectedGroup} onClose={() => setSelectedGroup(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{messages.paymentDetailsDialogTitle}</DialogTitle>
        <DialogContent dividers>
          <MDTypography
            component="pre"
            variant="body2"
            sx={{ whiteSpace: "pre-wrap", fontFamily: "inherit", m: 0 }}
          >
            {selectedGroup?.paymentComments}
          </MDTypography>
        </DialogContent>
      </Dialog>
    </>
  );
}
